/*
 *  This file hrone-snackbar-ref.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2025
 *  (C) 2015-2025. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { Observable, Subject } from 'rxjs';
import { IHroneSnackbarConfig, THroneSnackbarDismissReason } from './hrone-snackbar.model';

/**
 * Handle returned by HroneSnackbarService.Alert({ ...config }) for controlling one snackbar instance.
 * Calls on a ref whose snackbar is already gone (dismissed or replaced) are no-ops.
 */
export class HroneSnackbarRef {
  private readonly _afterDismissed = new Subject<THroneSnackbarDismissReason>();

  constructor(
    private readonly _dismissFn: () => void,
    private readonly _updateFn: (config: IHroneSnackbarConfig) => void,
  ) {}

  /** Dismiss this snackbar (with exit animation). */
  dismiss(): void {
    this._dismissFn();
  }

  /**
   * Merge new config into the visible snackbar and restart its timer from the merged `duration`.
   * Typical use: turn a 'pending' snackbar into 'success' / 'danger' once work finishes.
   */
  update(config: IHroneSnackbarConfig): void {
    this._updateFn(config);
  }

  /** Emits once (then completes) after the snackbar is removed from the DOM. */
  afterDismissed(): Observable<THroneSnackbarDismissReason> {
    return this._afterDismissed.asObservable();
  }

  /** @internal Called by HroneSnackbarService after removal. */
  _notifyDismissed(reason: THroneSnackbarDismissReason): void {
    this._afterDismissed.next(reason);
    this._afterDismissed.complete();
  }
}
